/**
 * L1J Web Client - StatusBar Component (Lineage Classic Style)
 *
 * 화면 하단 중앙에 가로 바로 표시되는 HP/MP 바.
 * 빨간 HP 바와 파란 MP 바가 가운데 장식(또는 구분선)을 사이에 두고 배치된다.
 * 카메라 스크롤과 무관하게 화면에 고정(setScrollFactor(0)).
 *
 * 세로 구성 (컨테이너 원점 = HP/MP 바의 세로 중앙):
 *   y -16 ~ +16 : HP 바(왼쪽) | 구분선 | MP 바(오른쪽), 바 안에 라벨·수치·레벨 칩
 *   y +18 ~ +23 : EXP 바 (전체 폭)
 *   y +25 ~     : 하단 정보줄 — 왼쪽 끝 성향(Lawful), 오른쪽 끝 EXP%
 *
 * 바 안쪽은 좌우 양끝에서 수치 텍스트가 자라 들어오므로, 수치와 경쟁하지 않는
 * 보조 표기(성향)는 바 바깥의 하단 정보줄에 둔다.
 */

import Phaser from 'phaser';

/**
 * setStatus에 넘기는 갱신 데이터.
 *
 * **모든 필드가 선택**이다 — 서버는 HP와 MP를 각각 다른 패킷으로 통지하므로
 * (S_HpUpdate 42 = {currentHp, maxHp}, S_MpUpdate 73 = {currentMp, maxMp}),
 * 한쪽만 바뀐 갱신에서 반대쪽 현재값을 억지로 채워 넣게 만들면 안 된다.
 * 넘기지 않은 필드는 직전 값을 그대로 유지한다.
 */
export interface StatusBarData {
  hp?: number;
  maxHp?: number;
  mp?: number;
  maxMp?: number;
  level?: number;
  exp?: number;
  maxExp?: number;
  lawful?: number;
}

export class StatusBar extends Phaser.GameObjects.Container {
  /** 가로 바 전체 너비 */
  private barWidth: number = 380;
  /** 가로 바 전체 높이 */
  private barHeight: number = 32;

  /** 장식/구분선 폭 (가운데) */
  private centerGutter: number = 24;

  // ── 레이아웃 상수 ──
  // 배치를 손으로 검산할 수 있도록 좌표에 쓰이는 값은 전부 여기에 모아 둔다.
  // (컨테이너 로컬 좌표계이고 스케일을 걸지 않으므로 해상도와 무관하게 고정이다)

  /** 장식 프레임이 바 바깥으로 더 나가는 여백 */
  private readonly framePadding: number = 6;
  /** 바 하단과 EXP 바 사이 간격 */
  private readonly expBarGap: number = 2;
  /** EXP 바 높이 */
  private readonly expBarHeight: number = 5;
  /** EXP 바와 하단 정보줄(성향 / EXP%) 사이 간격 */
  private readonly footerGap: number = 2;
  /** HP 바 안쪽 레벨 칩의 x 오프셋 (HP 라벨 폭을 피한 위치) */
  private readonly levelChipOffset: number = 60;

  private hpBar!: Phaser.GameObjects.Graphics;
  private mpBar!: Phaser.GameObjects.Graphics;
  private hpText!: Phaser.GameObjects.Text;
  private mpText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private expBar!: Phaser.GameObjects.Graphics;
  private expText!: Phaser.GameObjects.Text;
  private lawfulText!: Phaser.GameObjects.Text;

  private currentHp: number = 0;
  private currentMaxHp: number = 1;
  private currentMp: number = 0;
  private currentMaxMp: number = 1;

  // 바 색상 (라니지 클래식 톤)
  private readonly hpColor = 0xcc0000;        // HP 메인 빨강
  private readonly hpColorHi = 0xff3333;      // HP 그라데이션 하이라이트
  private readonly hpLowColor = 0xaa0000;
  private readonly mpColor = 0x0033cc;        // MP 메인 파랑
  private readonly mpColorHi = 0x3366ff;      // MP 그라데이션 하이라이트
  private readonly mpLowColor = 0x660066;
  private readonly expColor = 0xffd700;       // EXP 골드
  private readonly frameColor = 0x4a3a1a;     // 황금 프레임

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);

    // 카메라도 스크롤되어도 HUD는 화면에 고정
    this.setScrollFactor(0);

    this.createUI();
    this.setDepth(100);
  }

  /** 바 좌우 절반 폭 (좌표 계산의 기준점) */
  private get halfWidth(): number {
    return this.barWidth / 2;
  }

  /** EXP 바 상단 y (바 하단 바로 아래) */
  private get expBarTop(): number {
    return this.barHeight / 2 + this.expBarGap;
  }

  /** 하단 정보줄 상단 y (EXP 바 아래) — 성향 라벨과 EXP% 텍스트가 놓이는 줄 */
  private get footerTop(): number {
    return this.expBarTop + this.expBarHeight + this.footerGap;
  }

  private createUI(): void {
    const halfW = this.halfWidth;
    const barH = this.barHeight;
    const pad = this.framePadding;

    // ── 외곽 프레임 (장식용 황금 테두리) ──
    const frame = this.scene.add.graphics();
    frame.fillStyle(0x1a1a2e, 0.85);
    frame.fillRoundedRect(-halfW - pad, -barH / 2 - pad, this.barWidth + pad * 2, barH + pad * 2, 6);
    frame.lineStyle(2, this.frameColor, 1);
    frame.strokeRoundedRect(-halfW - pad, -barH / 2 - pad, this.barWidth + pad * 2, barH + pad * 2, 6);
    this.add(frame);

    // ── HP 영역 (왼쪽) ──
    const hpX0 = -halfW;
    const hpW = halfW - this.centerGutter / 2;

    // HP 배경
    const hpBg = this.scene.add.graphics();
    hpBg.fillStyle(0x1a0000, 1);
    hpBg.fillRoundedRect(hpX0, -barH / 2, hpW, barH, 3);
    this.add(hpBg);

    // HP 바
    this.hpBar = this.scene.add.graphics();
    this.add(this.hpBar);

    // HP 라벨 (바 안쪽 왼쪽)
    const hpLabel = this.scene.add.text(hpX0 + 8, 0, 'HP', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    hpLabel.setOrigin(0, 0.5);
    hpLabel.setName('hpLabel');
    this.add(hpLabel);

    // HP 텍스트 (숫자)
    this.hpText = this.scene.add.text(hpX0 + hpW - 8, 0, '0 / 0', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    this.hpText.setOrigin(1, 0.5);
    this.add(this.hpText);

    // ── 가운데 장식 (구분선) ──
    const center = this.scene.add.graphics();
    center.fillStyle(this.frameColor, 1);
    center.fillRect(-this.centerGutter / 2, -barH / 2 + 4, this.centerGutter, barH - 8);
    center.lineStyle(1, 0xffd700, 0.5);
    center.strokeRect(-this.centerGutter / 2, -barH / 2 + 4, this.centerGutter, barH - 8);
    this.add(center);

    // ── MP 영역 (오른쪽) ──
    const mpX0 = this.centerGutter / 2;
    const mpW = halfW - this.centerGutter / 2;

    // MP 배경
    const mpBg = this.scene.add.graphics();
    mpBg.fillStyle(0x00001a, 1);
    mpBg.fillRoundedRect(mpX0, -barH / 2, mpW, barH, 3);
    this.add(mpBg);

    // MP 바
    this.mpBar = this.scene.add.graphics();
    this.add(this.mpBar);

    // MP 라벨
    const mpLabel = this.scene.add.text(mpX0 + 8, 0, 'MP', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    mpLabel.setOrigin(0, 0.5);
    this.add(mpLabel);

    // MP 텍스트
    this.mpText = this.scene.add.text(mpX0 + mpW - 8, 0, '0 / 0', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    this.mpText.setOrigin(1, 0.5);
    this.add(this.mpText);

    // ── 레벨 — HP 바 좌측 안쪽 (HP 라벨 + 레벨 영역에 표시) ──
    // HP 라벨(hpX0+8, 너비 ~20px) 뒤쪽 위치로 띄워 겹침 방지
    this.levelText = this.scene.add.text(hpX0 + this.levelChipOffset, 0, 'Lv.1', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
      backgroundColor: '#00000088',
      padding: { x: 3, y: 1 }
    });
    this.levelText.setOrigin(0, 0.5);
    this.add(this.levelText);

    // ── EXP 바 (가로 바 아래쪽 좁은 바) ──
    const expBg = this.scene.add.graphics();
    expBg.fillStyle(0x222200, 1);
    expBg.fillRoundedRect(-halfW, this.expBarTop, this.barWidth, this.expBarHeight, 2);
    this.add(expBg);

    this.expBar = this.scene.add.graphics();
    this.add(this.expBar);

    // ── 하단 정보줄 — 성향(왼쪽 끝) / EXP%(오른쪽 끝) ──
    //
    // 성향 라벨은 예전에 MP 바 안쪽(mpX0 + 60)에 있었으나 MP 수치 텍스트와
    // 겹쳐서(MP 수치는 mpX0 + mpW - 8 에 우측 정렬 → 4자리 값이면 왼쪽 끝이
    // mpX0 + 101 근처까지 밀려온다) 판독이 불가능했다. 바 바깥의 이 줄로 내리면
    // MP 바 영역(x 12~190, y -16~16)과 x·y 어느 쪽으로도 겹치지 않는다.
    // 좌표가 전부 컨테이너 로컬 상수라서 화면 해상도와 무관하게 성립한다.
    this.lawfulText = this.scene.add.text(-halfW, this.footerTop, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
      fontStyle: 'bold',
      backgroundColor: '#00000088',
      padding: { x: 3, y: 1 }
    });
    this.lawfulText.setOrigin(0, 0);
    this.add(this.lawfulText);

    // EXP 텍스트 (오른쪽 끝) — 성향 라벨과 같은 줄, 반대쪽 끝
    this.expText = this.scene.add.text(halfW, this.footerTop, '0%', {
      fontSize: '9px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700'
    });
    this.expText.setOrigin(1, 0);
    this.add(this.expText);
  }

  /**
   * 원점에서 위쪽으로 차지하는 높이 (장식 프레임 포함).
   *
   * 이 컨테이너는 원점이 바 중앙이고 위아래로 차지하는 높이가 다르다 —
   * 아래에는 EXP 바와 퍼센트 텍스트가 더 붙는다. 화면 가장자리나 다른 UI에
   * 맞붙일 때 barHeight만 보고 계산하면 어긋난다.
   */
  public getExtentAbove(): number {
    return this.barHeight / 2 + this.framePadding;
  }

  /** 원점에서 아래쪽으로 차지하는 높이 (EXP 바 + 하단 정보줄 포함). */
  public getExtentBelow(): number {
    // 하단 정보줄에는 성향 라벨(11px + 배경 패딩)과 EXP%(9px)가 나란히 놓인다.
    // 둘 중 큰 쪽이 실제 아래쪽 끝이다.
    const footerHeight = Math.max(this.lawfulText.height, this.expText.height);
    return this.footerTop + footerHeight;
  }

  /**
   * 최대치로 쓸 수 있는 값인지. 0이나 음수를 그대로 나눗셈에 쓰면
   * Infinity/NaN이 되어 "0 / 0"인데 바가 꽉 찬 것처럼 보인다.
   * (own_char_status가 도착하기 전 hp/mp 갱신이 먼저 오는 경우가 있다.)
   */
  private static isUsableMax(value: number | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
  }

  /**
   * HP 갱신 (S_HpUpdate 42 대응). maxHp를 생략하면 직전 최대치를 유지한다.
   */
  public setHp(hp: number, maxHp?: number): void {
    this.setStatus({ hp, maxHp });
  }

  /**
   * MP 갱신 (S_MpUpdate 73 대응). setHp와 대칭 —
   * 게이트웨이가 mp_update를 { currentMp, maxMp } 객체로 보내므로
   * 최대치도 함께 반영해야 MP 바가 "n / 0"에서 멈추지 않는다.
   */
  public setMp(mp: number, maxMp?: number): void {
    this.setStatus({ mp, maxMp });
  }

  /**
   * 상태 데이터 업데이트
   */
  public setStatus(data: StatusBarData): void {
    if (data.hp !== undefined) this.currentHp = data.hp;
    if (StatusBar.isUsableMax(data.maxHp)) this.currentMaxHp = data.maxHp;
    this.updateHpBar();

    if (data.mp !== undefined) this.currentMp = data.mp;
    // HP와 대칭 — maxMp도 같은 조건으로 반영한다. 이 줄이 빠져 있으면
    // MP 최대치가 초기값(1)에 묶여 바가 항상 꽉 찬 것처럼 보인다.
    if (StatusBar.isUsableMax(data.maxMp)) this.currentMaxMp = data.maxMp;
    this.updateMpBar();

    if (data.level !== undefined) {
      this.levelText.setText(`Lv.${data.level}`);
    }

    if (data.exp !== undefined && data.maxExp !== undefined && data.maxExp > 0) {
      this.updateExpBar(data.exp, data.maxExp);
    }

    if (data.lawful !== undefined) {
      this.updateLawful(data.lawful);
    }
  }

  /**
   * HP 바 업데이트
   */
  private updateHpBar(): void {
    this.hpBar.clear();
    const halfW = this.barWidth / 2;
    const barH = this.barHeight;
    const hpX0 = -halfW;
    const hpW = halfW - this.centerGutter / 2;

    const percent = Math.min(Math.max(this.currentHp / this.currentMaxHp, 0), 1);
    const fillWidth = hpW * percent;

    // 게이지는 폭이 있을 때만 그린다. **텍스트는 그와 무관하게 항상 갱신한다** —
    // 여기서 조기 return하면 HP가 0이 된 순간 숫자가 직전 값에 멈춘다.
    // 사망 시 서버가 S_HpUpdate로 1을 보내고(S_HpUpdate의 hpRange가 1~32767로
    // 클램프한다) 뒤이어 S_OwnCharStatus가 진짜 0을 주는데, 그 0을 그리려다
    // return하는 바람에 "1 / 89"가 화면에 남았다.
    if (fillWidth > 0) {
      // HP 비율에 따른 색상 변경
      const color = percent < 0.3 ? this.hpLowColor : this.hpColor;
      this.hpBar.fillStyle(color, 1);
      this.hpBar.fillRoundedRect(hpX0, -barH / 2, fillWidth, barH, 3);

      // 상단 하이라이트 (그라데이션 느낌)
      if (percent >= 0.3) {
        this.hpBar.fillStyle(this.hpColorHi, 0.5);
        this.hpBar.fillRoundedRect(hpX0, -barH / 2, fillWidth, barH / 3, 3);
      }
    }

    // HP 텍스트
    this.hpText.setText(`${Math.floor(this.currentHp)} / ${Math.floor(this.currentMaxHp)}`);
  }

  /**
   * MP 바 업데이트
   */
  private updateMpBar(): void {
    this.mpBar.clear();
    const halfW = this.barWidth / 2;
    const barH = this.barHeight;
    const mpX0 = this.centerGutter / 2;
    const mpW = halfW - this.centerGutter / 2;

    const percent = Math.min(Math.max(this.currentMp / this.currentMaxMp, 0), 1);
    const fillWidth = mpW * percent;

    // HP와 같은 이유로 텍스트 갱신은 게이지 폭과 분리한다 (updateHpBar 주석 참조).
    if (fillWidth > 0) {
      const color = percent < 0.2 ? this.mpLowColor : this.mpColor;
      this.mpBar.fillStyle(color, 1);
      this.mpBar.fillRoundedRect(mpX0, -barH / 2, fillWidth, barH, 3);

      if (percent >= 0.2) {
        this.mpBar.fillStyle(this.mpColorHi, 0.5);
        this.mpBar.fillRoundedRect(mpX0, -barH / 2, fillWidth, barH / 3, 3);
      }
    }

    this.mpText.setText(`${Math.floor(this.currentMp)} / ${Math.floor(this.currentMaxMp)}`);
  }

  /**
   * EXP 바 업데이트
   */
  private updateExpBar(exp: number, maxExp: number): void {
    this.expBar.clear();
    const halfW = this.halfWidth;

    const percent = Math.min(Math.max(exp / maxExp, 0), 1);
    const fillWidth = this.barWidth * percent;

    // HP와 같은 이유로 텍스트 갱신은 게이지 폭과 분리한다 (updateHpBar 주석 참조).
    if (fillWidth > 0) {
      this.expBar.fillStyle(this.expColor, 1);
      this.expBar.fillRoundedRect(-halfW, this.expBarTop, fillWidth, this.expBarHeight, 2);
    }

    this.expText.setText(`${Math.floor(percent * 100)}%`);
  }

  /**
   * Lawful 값 업데이트 (라니지 클래식과 동일 임계값)
   */
  private updateLawful(lawful: number): void {
    let text: string;
    let color: string;

    if (lawful >= 750) {
      text = 'Hero';
      color = '#ffd700';
    } else if (lawful >= 500) {
      text = 'Knight';
      color = '#44ff44';
    } else if (lawful >= 0) {
      text = 'Neutral';
      color = '#aaaaaa';
    } else {
      text = 'Chaotic';
      color = '#ff4444';
    }

    this.lawfulText.setText(text);
    this.lawfulText.setStyle({ color });
  }

  /**
   * HP/MP 만 변경 시 애니메이션
   */
  public animateChange(
    type: 'hp' | 'mp',
    oldValue: number,
    newValue: number,
    duration: number = 300
  ): void {
    const startTime = this.scene.time.now;
    const bar = type === 'hp' ? this.hpBar : this.mpBar;
    const maxVal = type === 'hp' ? this.currentMaxHp : this.currentMaxMp;
    const halfW = this.barWidth / 2;
    const barH = this.barHeight;
    const x0 = type === 'hp' ? -halfW : this.centerGutter / 2;
    const fullW = halfW - this.centerGutter / 2;

    const tween = {
      update: () => {
        const elapsed = this.scene.time.now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentValue = oldValue + (newValue - oldValue) * progress;

        bar.clear();

        const percent = Math.min(Math.max(currentValue / maxVal, 0), 1);
        const fillWidth = fullW * percent;
        if (fillWidth <= 0) return;

        const color = type === 'hp'
          ? (percent < 0.3 ? this.hpLowColor : this.hpColor)
          : (percent < 0.2 ? this.mpLowColor : this.mpColor);
        bar.fillStyle(color, 1);
        bar.fillRoundedRect(x0, -barH / 2, fillWidth, barH, 3);
      },
      complete: () => {
        if (type === 'hp') this.currentHp = newValue;
        else this.currentMp = newValue;
      }
    };

    this.scene.events.on('update', tween.update);
    this.scene.time.delayedCall(duration, () => {
      this.scene.events.off('update', tween.update);
      tween.complete();
    });
  }

  /**
   * 데미지 표시 효과
   */
  public showDamage(amount: number, isHeal: boolean = false): void {
    const text = this.scene.add.text(0, -28, isHeal ? `+${amount}` : `-${amount}`, {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: isHeal ? '#44ff44' : '#ff4444',
      fontStyle: 'bold'
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);

    this.scene.tweens.add({
      targets: text,
      y: -58,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => text.destroy()
    });
  }

  /**
   * 현재 상태 가져오기
   */
  public getStatus(): StatusBarData {
    return {
      hp: this.currentHp,
      maxHp: this.currentMaxHp,
      mp: this.currentMp,
      maxMp: this.currentMaxMp
    };
  }
}
